/**
 * Email Notification Settings — Admin Only
 * Three tabs: SMTP Configuration / Event Routing / Send Log
 * Matches CCC4CS theme: red→blue gradient, MUI components, inline styles like UserManagement.js
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';

// ===== AUTH =====
const getAuthConfig = () => {
  const token = localStorage.getItem('token');
  return { headers: { 'Authorization': `Bearer ${token}` }, withCredentials: true };
};

// ===== ENDPOINTS =====
const EP = {
  SETTINGS: `${API_BASE_URL}/email_settings.php`,
  EVENTS:   `${API_BASE_URL}/email_events.php`,
  LOGS:     `${API_BASE_URL}/email_logs.php`,
};

// ===== INLINE STYLES (matches UserManagement.js conventions) =====
const S = {
  page: { padding: '20px 28px', background: '#f5f7fa', minHeight: '100vh' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  title: { fontSize: '22px', fontWeight: 600, color: '#1e293b', margin: 0 },
  subtitle: { fontSize: '13px', color: '#64748b', marginTop: '4px' },

  tabs: { display: 'flex', gap: '4px', borderBottom: '2px solid #e2e8f0', background: 'white', borderRadius: '12px 12px 0 0', padding: '0 8px' },
  tab: { padding: '14px 24px', fontSize: '14px', fontWeight: 500, color: '#64748b', cursor: 'pointer', borderBottom: '3px solid transparent', display: 'flex', alignItems: 'center', gap: '8px', userSelect: 'none', transition: 'all 0.15s ease' },
  tabActive: { color: '#1565c0', borderBottomColor: '#1565c0', background: '#fafafa' },
  tabBadge: { background: '#e3f2fd', color: '#1565c0', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600 },

  card: { background: 'white', borderRadius: '0 0 12px 12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '24px', marginBottom: '16px' },
  cardSub: { background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', padding: '20px 24px', marginBottom: '16px' },
  sectionTitle: { fontSize: '15px', fontWeight: 600, color: '#1e293b', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' },
  sectionIcon: { width: '28px', height: '28px', background: 'linear-gradient(135deg, #d32f2f 0%, #1565c0 100%)', color: 'white', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' },

  formRow: { display: 'grid', gap: '16px', marginBottom: '14px' },
  field: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '12px', color: '#475569', fontWeight: 500 },
  input: { padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', fontFamily: 'inherit', background: 'white', outline: 'none' },
  select: { padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', fontFamily: 'inherit', background: 'white', outline: 'none', cursor: 'pointer' },
  helper: { fontSize: '11px', color: '#94a3b8' },
  inputWrap: { position: 'relative' },
  eyeBtn: { position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: '#64748b', userSelect: 'none', fontSize: '14px', background: 'transparent', border: 'none' },

  switchRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0' },
  switch: { position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer' },
  switchInput: { opacity: 0, width: 0, height: 0 },
  slider: (on) => ({ position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0, background: on ? '#43a047' : '#cbd5e1', borderRadius: '24px', transition: '0.2s' }),
  sliderKnob: (on) => ({ position: 'absolute', height: '18px', width: '18px', left: on ? '23px' : '3px', bottom: '3px', background: 'white', borderRadius: '50%', transition: '0.2s' }),

  btn: { padding: '10px 18px', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: '8px' },
  btnPrimary: { background: 'linear-gradient(135deg, #d32f2f 0%, #1565c0 100%)', color: 'white' },
  btnOutlined: { background: 'white', color: '#1565c0', border: '1px solid #1565c0' },
  btnDanger:   { background: 'white', color: '#d32f2f', border: '1px solid #d32f2f' },
  btnText:     { background: 'transparent', color: '#1565c0', border: 'none' },
  btnSm: { padding: '6px 12px', fontSize: '12px' },

  pill: (bg, fg) => ({ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 500, background: bg, color: fg }),

  table: { width: '100%', borderCollapse: 'collapse' },
  th: { background: '#fafafa', textAlign: 'left', padding: '12px 14px', fontSize: '11px', fontWeight: 600, color: '#475569', borderBottom: '1px solid #e2e8f0', textTransform: 'uppercase', letterSpacing: '0.4px' },
  td: { padding: '12px 14px', borderBottom: '1px solid #f1f5f9', fontSize: '13px', verticalAlign: 'middle' },
  eventName: { fontFamily: '"Roboto Mono", "Consolas", monospace', fontSize: '12px', color: '#1565c0', fontWeight: 500 },
  eventDesc: { fontSize: '11px', color: '#64748b', marginTop: '2px' },

  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '14px', marginBottom: '20px' },
  statCard: (color) => ({ background: 'white', padding: '14px 18px', borderRadius: '10px', borderLeft: `4px solid ${color}`, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }),
  statLabel: { fontSize: '11px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 500, marginBottom: '4px' },
  statValue: { fontSize: '22px', fontWeight: 600, color: '#1e293b' },

  filterBar: { display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', padding: '12px 0', marginBottom: '12px' },

  alert: (kind) => ({ padding: '12px 16px', borderRadius: '6px', fontSize: '13px', marginBottom: '14px', borderLeft: `4px solid ${kind === 'success' ? '#43a047' : kind === 'error' ? '#d32f2f' : '#1565c0'}`, background: kind === 'success' ? '#e8f5e9' : kind === 'error' ? '#ffebee' : '#e3f2fd', color: kind === 'success' ? '#1b5e20' : kind === 'error' ? '#b71c1c' : '#0d47a1' }),

  modal: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  modalBox: { background: 'white', borderRadius: '10px', width: '100%', maxWidth: '600px', maxHeight: '90vh', overflow: 'auto' },
  modalHead: { padding: '18px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  modalBody: { padding: '20px 24px' },
  modalFoot: { padding: '14px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', gap: '10px', justifyContent: 'flex-end', background: '#fafafa' },
};

// ===== Toggle switch component =====
const Toggle = ({ checked, onChange, disabled }) => (
  <label style={{ ...S.switch, opacity: disabled ? 0.5 : 1 }}>
    <input type="checkbox" style={S.switchInput} checked={!!checked} disabled={disabled} onChange={e => onChange(e.target.checked)} />
    <span style={S.slider(!!checked)}>
      <span style={S.sliderKnob(!!checked)} />
    </span>
  </label>
);

// ============================================================================
//  MAIN COMPONENT
// ============================================================================
const EmailNotificationSettings = ({ user }) => {
  const [activeTab, setActiveTab] = useState('smtp');
  const [globalAlert, setGlobalAlert] = useState(null);

  // Permission gate
  const isAdmin = user?.role === 'admin';
  if (!isAdmin) {
    return (
      <div style={S.page}>
        <div style={S.alert('error')}>Access denied — admin role required to view email notification settings.</div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <h1 style={S.title}>📧 Email Notifications</h1>
          <div style={S.subtitle}>Configure SMTP, route events to recipients, monitor delivery</div>
        </div>
      </div>

      {globalAlert && <div style={S.alert(globalAlert.kind)}>{globalAlert.text}</div>}

      <div style={S.tabs}>
        <div style={{ ...S.tab, ...(activeTab === 'smtp' ? S.tabActive : {}) }} onClick={() => setActiveTab('smtp')}>
          ⚙ SMTP Configuration
        </div>
        <div style={{ ...S.tab, ...(activeTab === 'events' ? S.tabActive : {}) }} onClick={() => setActiveTab('events')}>
          🔔 Event Routing
        </div>
        <div style={{ ...S.tab, ...(activeTab === 'logs' ? S.tabActive : {}) }} onClick={() => setActiveTab('logs')}>
          📋 Send Log
        </div>
      </div>

      <div style={S.card}>
        {activeTab === 'smtp'   && <SmtpTab   onAlert={setGlobalAlert} />}
        {activeTab === 'events' && <EventsTab onAlert={setGlobalAlert} />}
        {activeTab === 'logs'   && <LogsTab   onAlert={setGlobalAlert} />}
      </div>
    </div>
  );
};

// ============================================================================
//  TAB 1 — SMTP CONFIGURATION
// ============================================================================
const SmtpTab = ({ onAlert }) => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [testRecipient, setTestRecipient] = useState('');
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(EP.SETTINGS, getAuthConfig());
      const s = r.data.settings || {};
      const flat = {};
      Object.keys(s).forEach(k => { flat[k] = s[k].setting_value; });
      setSettings(flat);
      setTestRecipient(flat.smtp_test_recipient || '');
    } catch (err) {
      onAlert({ kind: 'error', text: 'Failed to load SMTP settings: ' + (err.response?.data?.error || err.message) });
    } finally {
      setLoading(false);
    }
  }, [onAlert]);

  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setSettings(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      // Don't send the masked password back as-is
      const payload = { ...settings };
      if (payload.smtp_password === '••••••••••••') delete payload.smtp_password;

      const r = await axios.post(EP.SETTINGS, payload, getAuthConfig());
      onAlert({ kind: 'success', text: r.data.message || 'Settings saved' });
      await load();
    } catch (err) {
      onAlert({ kind: 'error', text: 'Save failed: ' + (err.response?.data?.error || err.message) });
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTestResult(null);
    try {
      const r = await axios.post(`${EP.SETTINGS}?action=test`, { recipient: testRecipient }, getAuthConfig());
      setTestResult({ ok: true, msg: r.data.message });
    } catch (err) {
      setTestResult({ ok: false, msg: err.response?.data?.error || err.message });
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading SMTP settings…</div>;

  const masterEnabled = settings.email_master_enabled === '1';

  return (
    <div>
      {/* Master switch */}
      <div style={S.cardSub}>
        <div style={S.switchRow}>
          <div>
            <div style={S.sectionTitle}><span style={S.sectionIcon}>⚡</span>Master switch</div>
            <div style={S.helper}>Globally enable or disable all outbound email from the system</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={S.pill(masterEnabled ? '#e8f5e9' : '#f1f5f9', masterEnabled ? '#2e7d32' : '#64748b')}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor' }} />
              {masterEnabled ? 'Active' : 'Disabled'}
            </span>
            <Toggle checked={masterEnabled} onChange={v => setField('email_master_enabled', v ? '1' : '0')} />
          </div>
        </div>
      </div>

      {/* SMTP server */}
      <div style={S.cardSub}>
        <div style={S.sectionTitle}><span style={S.sectionIcon}>🌐</span>SMTP server</div>
        <div style={{ ...S.formRow, gridTemplateColumns: '2fr 1fr 1fr' }}>
          <div style={S.field}>
            <label style={S.label}>Host</label>
            <input style={S.input} value={settings.smtp_host || ''} onChange={e => setField('smtp_host', e.target.value)} />
            <span style={S.helper}>e.g. smtp.office365.com, smtp.gmail.com</span>
          </div>
          <div style={S.field}>
            <label style={S.label}>Port</label>
            <input style={S.input} type="number" value={settings.smtp_port || ''} onChange={e => setField('smtp_port', e.target.value)} />
            <span style={S.helper}>587 (TLS) or 465 (SSL)</span>
          </div>
          <div style={S.field}>
            <label style={S.label}>Encryption</label>
            <select style={S.select} value={settings.smtp_encryption || 'TLS'} onChange={e => setField('smtp_encryption', e.target.value)}>
              <option>TLS</option>
              <option>SSL</option>
              <option>NONE</option>
            </select>
          </div>
        </div>
      </div>

      {/* Auth */}
      <div style={S.cardSub}>
        <div style={S.sectionTitle}><span style={S.sectionIcon}>🔐</span>Authentication</div>
        <div style={{ ...S.formRow, gridTemplateColumns: '1fr 1fr' }}>
          <div style={S.field}>
            <label style={S.label}>SMTP Username</label>
            <input style={S.input} value={settings.smtp_username || ''} onChange={e => setField('smtp_username', e.target.value)} />
          </div>
          <div style={S.field}>
            <label style={S.label}>SMTP Password</label>
            <div style={S.inputWrap}>
              <input style={S.input} type={showPwd ? 'text' : 'password'} value={settings.smtp_password || ''} onChange={e => setField('smtp_password', e.target.value)} placeholder="Leave blank to keep existing" />
              <button style={S.eyeBtn} onClick={() => setShowPwd(!showPwd)} type="button">{showPwd ? '🙈' : '👁'}</button>
            </div>
            <span style={S.helper}>Stored encrypted. Leave the masked dots untouched to preserve existing password.</span>
          </div>
        </div>
      </div>

      {/* Sender identity */}
      <div style={S.cardSub}>
        <div style={S.sectionTitle}><span style={S.sectionIcon}>📧</span>Sender identity</div>
        <div style={{ ...S.formRow, gridTemplateColumns: '1fr 1fr' }}>
          <div style={S.field}>
            <label style={S.label}>From Email</label>
            <input style={S.input} type="email" value={settings.smtp_from_email || ''} onChange={e => setField('smtp_from_email', e.target.value)} />
          </div>
          <div style={S.field}>
            <label style={S.label}>From Name (display)</label>
            <input style={S.input} value={settings.smtp_from_name || ''} onChange={e => setField('smtp_from_name', e.target.value)} />
          </div>
        </div>
        <div style={{ ...S.formRow, gridTemplateColumns: '1fr', marginTop: 12 }}>
          <div style={S.field}>
            <label style={S.label}>System URL (used in email links)</label>
            <input style={S.input} type="url" value={settings.smtp_system_url || ''} onChange={e => setField('smtp_system_url', e.target.value)} />
          </div>
        </div>
      </div>

      {/* Test */}
      <div style={S.cardSub}>
        <div style={S.sectionTitle}><span style={S.sectionIcon}>🧪</span>Test connection</div>
        <div style={{ ...S.formRow, gridTemplateColumns: '2fr 1fr', alignItems: 'flex-end' }}>
          <div style={S.field}>
            <label style={S.label}>Recipient email</label>
            <input style={S.input} type="email" value={testRecipient} onChange={e => setTestRecipient(e.target.value)} />
          </div>
          <button style={{ ...S.btn, ...S.btnOutlined, height: 40 }} onClick={sendTest}>▶ Send Test Email</button>
        </div>
        {testResult && (
          <div style={{ ...S.alert(testResult.ok ? 'success' : 'error'), marginTop: 12 }}>
            {testResult.ok ? '✓ ' : '✗ '}{testResult.msg}
          </div>
        )}
      </div>

      {/* Save bar */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 8 }}>
        <button style={{ ...S.btn, ...S.btnText }} onClick={load}>Reset</button>
        <button style={{ ...S.btn, ...S.btnPrimary }} disabled={saving} onClick={save}>{saving ? 'Saving…' : '💾 Save Configuration'}</button>
      </div>
    </div>
  );
};

// ============================================================================
//  TAB 2 — EVENT ROUTING
// ============================================================================
const EventsTab = ({ onAlert }) => {
  const [data, setData] = useState({ events: [], roles: [], users: [], capabilities: [] });
  const [loading, setLoading] = useState(true);
  const [editEvent, setEditEvent] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(EP.EVENTS, getAuthConfig());
      setData(r.data);
    } catch (err) {
      onAlert({ kind: 'error', text: 'Failed to load events: ' + (err.response?.data?.error || err.message) });
    } finally {
      setLoading(false);
    }
  }, [onAlert]);

  useEffect(() => { load(); }, [load]);

  const toggleEnabled = async (ev, enabled) => {
    try {
      await axios.post(EP.EVENTS, { ...ev, enabled: enabled ? 1 : 0, recipient_value: ev.recipient_value || [] }, getAuthConfig());
      onAlert({ kind: 'success', text: `${ev.event_key} ${enabled ? 'enabled' : 'disabled'}` });
      await load();
    } catch (err) {
      onAlert({ kind: 'error', text: 'Update failed: ' + (err.response?.data?.error || err.message) });
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading events…</div>;

  const categoryColors = {
    Approvals: { bg: '#e3f2fd', fg: '#0d47a1' },
    Inventory: { bg: '#fff3e0', fg: '#e65100' },
    Shipments: { bg: '#f3e5f5', fg: '#4a148c' },
  };

  return (
    <div>
      <div style={S.alert('info')}>
        <strong>How event routing works:</strong> When an event fires (e.g. a status change), the system looks up which recipients should receive an email. Toggle to enable/disable. Click <em>Edit</em> to choose recipients.
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={{ ...S.th, width: '30%' }}>Event</th>
              <th style={{ ...S.th, width: '10%' }}>Category</th>
              <th style={{ ...S.th, width: '10%', textAlign: 'center' }}>Enabled</th>
              <th style={{ ...S.th, width: '30%' }}>Recipients</th>
              <th style={{ ...S.th, width: '10%', textAlign: 'right' }}>Last sent</th>
              <th style={{ ...S.th, width: '10%', textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.events.map(ev => {
              const cc = categoryColors[ev.category] || { bg: '#f1f5f9', fg: '#475569' };
              const recipientCount = Array.isArray(ev.recipient_value) ? ev.recipient_value.length : 0;
              return (
                <tr key={ev.event_key}>
                  <td style={S.td}>
                    <div style={S.eventName}>{ev.event_key}</div>
                    <div style={S.eventDesc}>{ev.event_description}</div>
                  </td>
                  <td style={S.td}><span style={S.pill(cc.bg, cc.fg)}>{ev.category}</span></td>
                  <td style={{ ...S.td, textAlign: 'center' }}>
                    <Toggle checked={Number(ev.enabled) === 1} onChange={v => toggleEnabled(ev, v)} />
                  </td>
                  <td style={S.td}>
                    <span style={S.pill('#f3e5f5', '#6a1b9a')}>{modeLabel(ev.recipient_mode)}</span>
                    {recipientCount > 0 && <span style={{ marginLeft: 8, color: '#64748b', fontSize: 11 }}>{recipientCount} configured</span>}
                  </td>
                  <td style={{ ...S.td, textAlign: 'right', color: '#64748b', fontSize: 12 }}>
                    {ev.last_sent_at ? new Date(ev.last_sent_at.replace(' ', 'T')).toLocaleDateString() : '—'}
                  </td>
                  <td style={{ ...S.td, textAlign: 'right' }}>
                    <button style={{ ...S.btn, ...S.btnText, ...S.btnSm }} onClick={() => setEditEvent(ev)}>✏ Edit</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editEvent && (
        <RecipientEditorModal
          event={editEvent}
          roles={data.roles}
          users={data.users}
          capabilities={data.capabilities}
          onClose={() => setEditEvent(null)}
          onSaved={() => { setEditEvent(null); load(); onAlert({ kind: 'success', text: 'Event updated' }); }}
          onError={(msg) => onAlert({ kind: 'error', text: msg })}
        />
      )}
    </div>
  );
};

const modeLabel = (m) => {
  const map = {
    external: '📧 Email Addresses',
    users: 'Pick from Users',
    role: 'By Role',
    approvers: 'All Approvers',
    requester: 'Requester',
    capability: 'By Capability',
    perrequest: 'Per-Request List',
  };
  return map[m] || m;
};

// ===== Recipient editor modal =====
const RecipientEditorModal = ({ event, roles, users, capabilities, onClose, onSaved, onError }) => {
  const [form, setForm] = useState({
    enabled: Number(event.enabled) === 1,
    // If event has empty/null recipient list, default to 'external' (typed emails)
    // so admin lands directly on the add/remove pill UI — same UX for all events
    recipient_mode: (() => {
      const savedMode = event.recipient_mode;
      const savedValue = event.recipient_value;
      const isEmpty = !savedValue || (Array.isArray(savedValue) && savedValue.length === 0);
      // For empty events, always start with external (typing) UI regardless of saved mode
      if (isEmpty) return 'external';
      return savedMode || 'external';
    })(),
    recipient_value: event.recipient_value || [],
    priority_override_enabled: Number(event.priority_override_enabled) === 1,
    priority_high_recipients: event.priority_high_recipients || [],
    priority_urgent_recipients: event.priority_urgent_recipients || [],
  });
  const [saving, setSaving] = useState(false);

  const upd = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    // Warn if enabling an event but no recipients configured
    if (form.enabled) {
      const needsRecipients = ['external', 'users', 'role', 'capability'].includes(form.recipient_mode);
      const isEmpty = !form.recipient_value || form.recipient_value.length === 0;
      if (needsRecipients && isEmpty) {
        const proceed = window.confirm(
          'You have enabled this event but have not added any email recipients.\n\n' +
          'No emails will be sent. Save anyway?'
        );
        if (!proceed) return;
      }
    }
    setSaving(true);
    try {
      await axios.post(EP.EVENTS, { event_key: event.event_key, ...form }, getAuthConfig());
      onSaved();
    } catch (err) {
      onError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={S.modal} onClick={onClose}>
      <div style={S.modalBox} onClick={e => e.stopPropagation()}>
        <div style={S.modalHead}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 600 }}>Configure recipients</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
              Event: <span style={S.eventName}>{event.event_key}</span>
            </div>
          </div>
          <button style={{ background: 'transparent', border: 'none', fontSize: 22, cursor: 'pointer', color: '#64748b' }} onClick={onClose}>×</button>
        </div>

        <div style={S.modalBody}>
          <div style={S.switchRow}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 500 }}>Enabled</div>
              <div style={{ fontSize: 11, color: '#64748b' }}>If off, no email is sent for this event</div>
            </div>
            <Toggle checked={form.enabled} onChange={v => upd('enabled', v)} />
          </div>

          <div style={S.field}>
            <label style={S.label}>Recipient mode</label>
            <select style={S.select} value={form.recipient_mode} onChange={e => upd('recipient_mode', e.target.value)}>
              <option value="external">📧 Email Addresses (type & add)</option>
              <option value="users">Pick from Existing Users</option>
              <option value="role">By User Role</option>
              <option value="approvers">All Approvers (auto)</option>
              <option value="requester">Notify Requester / Creator</option>
              <option value="capability">By Approval Capability</option>
              <option value="perrequest">Per-Request List (approval events only)</option>
            </select>
          </div>

          <RecipientPicker mode={form.recipient_mode} value={form.recipient_value} onChange={v => upd('recipient_value', v)} roles={roles} users={users} capabilities={capabilities} />

          <div style={{ borderTop: '1px solid #e2e8f0', marginTop: 18, paddingTop: 14 }}>
            <div style={S.switchRow}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>Priority-based escalation</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>Add extra recipients for HIGH or URGENT requests</div>
              </div>
              <Toggle checked={form.priority_override_enabled} onChange={v => upd('priority_override_enabled', v)} />
            </div>

            {form.priority_override_enabled && (
              <div style={{ background: '#fff8e1', borderLeft: '3px solid #ff9800', padding: 12, borderRadius: 4, marginTop: 8 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: 12, alignItems: 'center', padding: '6px 0' }}>
                  <span style={S.pill('#fff3e0', '#e65100')}>HIGH</span>
                  <input style={{ ...S.input, fontSize: 12 }} placeholder="comma-separated emails" value={(form.priority_high_recipients || []).join(', ')} onChange={e => upd('priority_high_recipients', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: 12, alignItems: 'center', padding: '6px 0' }}>
                  <span style={S.pill('#ffebee', '#c62828')}>URGENT</span>
                  <input style={{ ...S.input, fontSize: 12 }} placeholder="comma-separated emails" value={(form.priority_urgent_recipients || []).join(', ')} onChange={e => upd('priority_urgent_recipients', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} />
                </div>
              </div>
            )}
          </div>
        </div>

        <div style={S.modalFoot}>
          <button style={{ ...S.btn, ...S.btnText }} onClick={onClose}>Cancel</button>
          <button style={{ ...S.btn, ...S.btnPrimary }} onClick={save} disabled={saving}>{saving ? 'Saving…' : '💾 Save'}</button>
        </div>
      </div>
    </div>
  );
};

// ===== External email picker (add/remove with pills) =====
const ExternalEmailPicker = ({ value, onChange }) => {
  const [input, setInput] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const arr = Array.isArray(value) ? value : [];

  const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const addEmail = () => {
    setError('');
    setInfo('');
    const raw = input.trim();
    if (!raw) return;

    // Extract ALL email-like tokens from anywhere in the text.
    // This handles every common paste format:
    //   plain:    alice@example.com, bob@example.com
    //   markdown: [alice@example.com](mailto:alice@example.com)
    //   outlook:  "Alice Smith" <alice@example.com>; bob@example.com
    //   mailto:   mailto:alice@example.com
    //   quoted:   'alice@example.com'
    const EXTRACT_RX = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
    const matches = raw.match(EXTRACT_RX) || [];

    if (matches.length === 0) {
      setError('Could not find any valid email addresses in your input. Each address must look like name@domain.com');
      return;
    }

    const added = [];
    const duplicatesInList = [];
    const duplicatesInPaste = [];

    for (const m of matches) {
      const email = m.toLowerCase();
      if (arr.includes(email)) {
        duplicatesInList.push(email);
        continue;
      }
      if (added.includes(email)) {
        duplicatesInPaste.push(email);
        continue;
      }
      added.push(email);
    }

    if (added.length > 0) {
      onChange([...arr, ...added]);
    }

    const parts = [];
    if (added.length > 0) parts.push(`✓ Added ${added.length} email${added.length > 1 ? 's' : ''}`);
    const totalDup = duplicatesInList.length + duplicatesInPaste.length;
    if (totalDup > 0) parts.push(`⚠ ${totalDup} duplicate${totalDup > 1 ? 's' : ''} skipped`);

    if (parts.length > 0) setInfo(parts.join(' · '));
    setInput('');
  };

  const removeEmail = (email) => {
    onChange(arr.filter(e => e !== email));
  };

  const clearAll = () => {
    if (arr.length === 0) return;
    if (window.confirm(`Remove all ${arr.length} email recipients?`)) {
      onChange([]);
      setInfo(`✓ Cleared ${arr.length} recipients`);
      setError('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addEmail();
    }
  };

  return (
    <div style={{ ...S.field, marginTop: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <label style={S.label}>External email recipients ({arr.length})</label>
        {arr.length > 0 && (
          <button type="button" onClick={clearAll}
            style={{ background: 'transparent', color: '#d32f2f', border: 'none', fontSize: 11, cursor: 'pointer', textDecoration: 'underline' }}>
            Clear all
          </button>
        )}
      </div>

      {/* Add row — textarea supports multi-email paste */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', alignItems: 'flex-start' }}>
        <textarea
          style={{ ...S.input, flex: 1, minHeight: '60px', resize: 'vertical', fontFamily: 'inherit' }}
          placeholder={'Type or paste emails in any format. Examples:\n\nalice@example.com, bob@example.com\n[alice@example.com](mailto:alice@example.com)\n"Alice Smith" <alice@example.com>; bob@example.com\n\nThen press Enter or click "+ Add"'}
          value={input}
          onChange={e => { setInput(e.target.value); setError(''); setInfo(''); }}
          onKeyDown={handleKeyDown}
        />
        <button
          type="button"
          style={{ ...S.btn, ...S.btnPrimary, whiteSpace: 'nowrap', height: '40px' }}
          onClick={addEmail}
        >
          + Add
        </button>
      </div>

      {error && <div style={{ ...S.alert('error'), padding: '8px 12px', marginBottom: 12 }}>{error}</div>}
      {info  && <div style={{ ...S.alert('success'), padding: '8px 12px', marginBottom: 12 }}>{info}</div>}

      {/* Email pills */}
      {arr.length === 0 ? (
        <div style={{
          padding: 20, textAlign: 'center', background: '#fafafa',
          borderRadius: 6, color: '#94a3b8', fontSize: 13,
          border: '1px dashed #cbd5e1'
        }}>
          No email addresses added yet. Type one or more emails above and click "+ Add".
        </div>
      ) : (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: '8px',
          padding: '12px', background: '#fafafa', borderRadius: '6px',
          maxHeight: '180px', overflowY: 'auto'
        }}>
          {arr.map(email => (
            <span
              key={email}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                padding: '6px 10px 6px 14px',
                background: 'white', border: '1px solid #cbd5e1',
                borderRadius: '20px', fontSize: '13px', color: '#1e293b'
              }}
            >
              <span>📧 {email}</span>
              <button
                type="button"
                onClick={() => removeEmail(email)}
                title="Remove this email"
                style={{
                  background: '#ef4444', color: 'white',
                  border: 'none', borderRadius: '50%',
                  width: '20px', height: '20px',
                  cursor: 'pointer', fontSize: '14px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  lineHeight: 1, padding: 0,
                }}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}

      <span style={S.helper}>
        Tip: paste a whole list at once — emails separated by commas, semicolons, spaces, or new lines all work.
        Press Enter to add. Click ✕ on any pill to remove. Don't forget to click 💾 Save below.
      </span>
    </div>
  );
};

const RecipientPicker = ({ mode, value, onChange, roles, users, capabilities }) => {
  const arr = Array.isArray(value) ? value : [];

  if (mode === 'approvers' || mode === 'requester' || mode === 'perrequest') {
    const explain = {
      approvers: 'All users where can_approve = 1 OR is_approver = 1 OR role = admin (and active, with email).',
      requester: 'The user who triggered the event will receive the email automatically.',
      perrequest: 'Reads approval_requests.can_be_approved_by JSON for the specific request. Falls back to all approvers if not set.',
    };
    return <div style={{ background: '#fafafa', padding: 12, borderRadius: 6, fontSize: 12, color: '#475569', marginTop: 12 }}>{explain[mode]}</div>;
  }

  if (mode === 'external') {
    return <ExternalEmailPicker value={arr} onChange={onChange} />;
  }

  if (mode === 'capability') {
    return (
      <div style={{ ...S.field, marginTop: 12 }}>
        <label style={S.label}>Match users with these capabilities</label>
        <div style={{ background: '#fafafa', padding: 12, borderRadius: 6 }}>
          {capabilities.map(c => (
            <label key={c} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', cursor: 'pointer' }}>
              <input type="checkbox" checked={arr.includes(c)} onChange={e => onChange(e.target.checked ? [...arr, c] : arr.filter(x => x !== c))} />
              <code style={{ background: '#fff', padding: '2px 6px', borderRadius: 3 }}>{c}</code>
            </label>
          ))}
        </div>
        <span style={S.helper}>Pulls from approval_assignments. Matches if ANY checked capability is true.</span>
      </div>
    );
  }

  if (mode === 'role') {
    return (
      <div style={{ ...S.field, marginTop: 12 }}>
        <label style={S.label}>Pick roles</label>
        <div style={{ background: '#fafafa', padding: 12, borderRadius: 6 }}>
          {roles.map(r => (
            <label key={r} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', margin: 2, background: arr.includes(r) ? '#e3f2fd' : 'white', borderRadius: 12, fontSize: 12, cursor: 'pointer', border: '1px solid #d1d5db' }}>
              <input type="checkbox" style={{ display: 'none' }} checked={arr.includes(r)} onChange={e => onChange(e.target.checked ? [...arr, r] : arr.filter(x => x !== r))} />
              {arr.includes(r) ? '✓ ' : ''}{r}
            </label>
          ))}
        </div>
      </div>
    );
  }

  if (mode === 'users') {
    return (
      <div style={{ ...S.field, marginTop: 12 }}>
        <label style={S.label}>Pick users ({arr.length} selected)</label>
        <div style={{ background: '#fafafa', padding: 12, borderRadius: 6, maxHeight: 220, overflow: 'auto' }}>
          {users.map(u => (
            <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0', cursor: 'pointer', fontSize: 12 }}>
              <input type="checkbox" checked={arr.includes(u.id)} onChange={e => onChange(e.target.checked ? [...arr, u.id] : arr.filter(x => x !== u.id))} />
              <span>{u.full_name || u.username}</span>
              <span style={{ color: '#64748b', fontSize: 11 }}>· {u.email}</span>
              <span style={S.pill('#f1f5f9', '#475569')}>{u.role}</span>
            </label>
          ))}
        </div>
      </div>
    );
  }

  return null;
};

// ============================================================================
//  TAB 3 — SEND LOG
// ============================================================================
const LogsTab = ({ onAlert }) => {
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageInfo, setPageInfo] = useState({ total: 0, pages: 0 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', event_key: '', from: '', to: '', q: '' });

  const loadStats = useCallback(async () => {
    try {
      const r = await axios.get(`${EP.LOGS}?action=stats`, getAuthConfig());
      setStats(r.data.stats);
    } catch {}
  }, []);

  const loadRows = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ action: 'list', page: p, per_page: 25, ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v)) });
      const r = await axios.get(`${EP.LOGS}?${params}`, getAuthConfig());
      setRows(r.data.rows || []);
      setPageInfo({ total: r.data.total, pages: r.data.pages });
    } catch (err) {
      onAlert({ kind: 'error', text: 'Failed to load logs: ' + (err.response?.data?.error || err.message) });
    } finally {
      setLoading(false);
    }
  }, [page, filters, onAlert]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadRows(page); }, [loadRows, page]);

  const retry = async (id) => {
    try {
      await axios.post(`${EP.LOGS}?action=retry`, { id }, getAuthConfig());
      onAlert({ kind: 'success', text: 'Retry sent' });
      loadRows(page); loadStats();
    } catch (err) {
      onAlert({ kind: 'error', text: 'Retry failed: ' + (err.response?.data?.error || err.message) });
    }
  };

  return (
    <div>
      {stats && (
        <div style={S.statsGrid}>
          <div style={S.statCard('#43a047')}>
            <div style={S.statLabel}>Sent today</div>
            <div style={S.statValue}>{stats.sent_today}</div>
          </div>
          <div style={S.statCard('#d32f2f')}>
            <div style={S.statLabel}>Failed today</div>
            <div style={S.statValue}>{stats.failed_today}</div>
          </div>
          <div style={S.statCard('#1565c0')}>
            <div style={S.statLabel}>Sent (30 days)</div>
            <div style={S.statValue}>{stats.sent_30d}</div>
          </div>
          <div style={S.statCard('#ff9800')}>
            <div style={S.statLabel}>Failed (30 days)</div>
            <div style={S.statValue}>{stats.failed_30d}</div>
          </div>
          <div style={S.statCard('#9e9e9e')}>
            <div style={S.statLabel}>Failure rate today</div>
            <div style={S.statValue}>{stats.failure_rate_today}%</div>
          </div>
        </div>
      )}

      <div style={S.filterBar}>
        <input style={{ ...S.input, flex: 1, minWidth: 200 }} placeholder="Search by recipient or subject…" value={filters.q} onChange={e => setFilters({ ...filters, q: e.target.value })} />
        <select style={S.select} value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })}>
          <option value="">All statuses</option><option value="SENT">Sent</option><option value="FAILED">Failed</option><option value="PENDING">Pending</option>
        </select>
        <input style={S.input} type="date" value={filters.from} onChange={e => setFilters({ ...filters, from: e.target.value })} />
        <input style={S.input} type="date" value={filters.to} onChange={e => setFilters({ ...filters, to: e.target.value })} />
        <button style={{ ...S.btn, ...S.btnOutlined, ...S.btnSm }} onClick={() => { setPage(1); loadRows(1); }}>↻ Apply</button>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading…</div>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={{ ...S.th, width: '5%' }}>ID</th>
                  <th style={{ ...S.th, width: '18%' }}>Event</th>
                  <th style={{ ...S.th, width: '20%' }}>Recipient</th>
                  <th style={{ ...S.th, width: '32%' }}>Subject</th>
                  <th style={{ ...S.th, width: '10%', textAlign: 'center' }}>Status</th>
                  <th style={{ ...S.th, width: '10%' }}>Sent</th>
                  <th style={{ ...S.th, width: '5%', textAlign: 'right' }}></th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={7} style={{ ...S.td, textAlign: 'center', color: '#64748b', padding: 40 }}>No log entries match these filters.</td></tr>
                ) : rows.map(r => (
                  <tr key={r.id}>
                    <td style={{ ...S.td, color: '#94a3b8', fontFamily: 'monospace', fontSize: 11 }}>#{r.id}</td>
                    <td style={S.td}><span style={S.eventName}>{r.event_key || r.notification_type}</span></td>
                    <td style={S.td}>{r.recipient_email}</td>
                    <td style={{ ...S.td, fontSize: 12 }}>{r.subject}</td>
                    <td style={{ ...S.td, textAlign: 'center' }}>
                      {r.sent_status === 'SENT'   && <span style={S.pill('#e8f5e9', '#2e7d32')}>SENT</span>}
                      {r.sent_status === 'FAILED' && <span style={S.pill('#ffebee', '#c62828')} title={r.error_message}>FAILED</span>}
                      {r.sent_status === 'PENDING'&& <span style={S.pill('#fff8e1', '#f57f17')}>PENDING</span>}
                    </td>
                    <td style={{ ...S.td, fontSize: 12, color: '#64748b' }}>{r.sent_at ? new Date(r.sent_at.replace(' ', 'T')).toLocaleString() : '—'}</td>
                    <td style={{ ...S.td, textAlign: 'right' }}>
                      {r.sent_status === 'FAILED' && (
                        <button style={{ ...S.btn, ...S.btnText, ...S.btnSm, color: '#d32f2f' }} onClick={() => retry(r.id)}>↻ Retry</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', fontSize: 12, color: '#475569' }}>
            <span>Showing page {page} of {pageInfo.pages || 1} · {pageInfo.total} entries</span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button style={{ ...S.btn, ...S.btnOutlined, ...S.btnSm }} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹ Prev</button>
              <button style={{ ...S.btn, ...S.btnOutlined, ...S.btnSm }} disabled={page >= (pageInfo.pages || 1)} onClick={() => setPage(p => p + 1)}>Next ›</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EmailNotificationSettings;
